import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import type { AccessRule } from '@/types/rules'
import { VALID_APPS, VALID_APIS, VALID_ROLES } from '@/types/rules'
import { fetchSites } from '@/services/api'

interface RuleEditDialogProps {
  rule: AccessRule | null
  open: boolean
  onClose: () => void
  onSave: (rule: AccessRule) => void
}

const emptyRule: AccessRule = {
  match: {},
  grants: {},
}

export default function RuleEditDialog({
  rule,
  open,
  onClose,
  onSave,
}: RuleEditDialogProps) {
  const [matchType, setMatchType] = useState<'email_domain' | 'email'>('email')
  const [matchValue, setMatchValue] = useState('')
  const [role, setRole] = useState('')
  const [selectedSites, setSelectedSites] = useState<string[]>([])
  const [selectedApps, setSelectedApps] = useState<string[]>([])
  const [appsAll, setAppsAll] = useState(false)
  const [selectedApis, setSelectedApis] = useState<string[]>([])
  const [apisAll, setApisAll] = useState(false)
  const [note, setNote] = useState('')
  const [availableSites, setAvailableSites] = useState<string[]>([])
  const [sitesLoading, setSitesLoading] = useState(true)

  useEffect(() => {
    setSitesLoading(true)
    fetchSites()
      .then(setAvailableSites)
      .catch((err) => console.error('Failed to fetch sites:', err))
      .finally(() => setSitesLoading(false))
  }, [])

  useEffect(() => {
    const r = rule ?? emptyRule
    if (r.match.email_domain) {
      setMatchType('email_domain')
      setMatchValue(r.match.email_domain)
    } else {
      setMatchType('email')
      setMatchValue(r.match.email ?? '')
    }
    setRole(r.grants.role ?? '')
    setSelectedSites(r.grants.sites ?? [])
    const apps = r.grants.apps ?? []
    if (apps.includes('*')) {
      setAppsAll(true)
      setSelectedApps([])
    } else {
      setAppsAll(false)
      setSelectedApps(apps)
    }
    const apis = r.grants.apis ?? []
    if (apis.includes('*')) {
      setApisAll(true)
      setSelectedApis([])
    } else {
      setApisAll(false)
      setSelectedApis(apis)
    }
    setNote(r.note ?? '')
  }, [rule, open])

  if (!open) return null

  const handleSave = () => {
    const newRule: AccessRule = {
      match:
        matchType === 'email_domain'
          ? { email_domain: matchValue }
          : { email: matchValue },
      grants: {},
    }
    if (role) newRule.grants.role = role
    if (selectedSites.length > 0) newRule.grants.sites = selectedSites
    if (appsAll) {
      newRule.grants.apps = ['*']
    } else if (selectedApps.length > 0) {
      newRule.grants.apps = selectedApps
    }
    if (apisAll) {
      newRule.grants.apis = ['*']
    } else if (selectedApis.length > 0) {
      newRule.grants.apis = selectedApis
    }
    const trimmedNote = note.trim()
    if (trimmedNote) newRule.note = trimmedNote
    onSave(newRule)
  }

  const toggleItem = (
    list: string[],
    setList: React.Dispatch<React.SetStateAction<string[]>>,
    item: string
  ) => {
    setList(
      list.includes(item) ? list.filter((i) => i !== item) : [...list, item]
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">
            {rule ? 'ルール編集' : 'ルール追加'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-5">
          {/* Match条件 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              マッチ条件
            </label>
            <div className="flex gap-3 mb-2">
              <label className="flex items-center gap-1.5 text-sm">
                <input
                  type="radio"
                  name="matchType"
                  checked={matchType === 'email'}
                  onChange={() => setMatchType('email')}
                  className="text-primary-600"
                />
                メールアドレス
              </label>
              <label className="flex items-center gap-1.5 text-sm">
                <input
                  type="radio"
                  name="matchType"
                  checked={matchType === 'email_domain'}
                  onChange={() => setMatchType('email_domain')}
                  className="text-primary-600"
                />
                ドメイン
              </label>
            </div>
            <input
              type="text"
              value={matchValue}
              onChange={(e) => setMatchValue(e.target.value)}
              placeholder={
                matchType === 'email' ? 'user@example.com' : 'hakuou.co.jp'
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* ロール */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ロール
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">なし</option>
              {VALID_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          {/* サイト */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              サイト
            </label>
            {sitesLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-1">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" />
                読み込み中...
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {availableSites
                  .filter((site) => !site.startsWith('sim-'))
                  .map((site) => (
                    <label
                      key={site}
                      className="flex items-center gap-1.5 text-sm bg-gray-50 px-2.5 py-1.5 rounded-md border border-gray-200 cursor-pointer hover:bg-gray-100"
                    >
                      <input
                        type="checkbox"
                        checked={selectedSites.includes(site)}
                        onChange={() =>
                          toggleItem(selectedSites, setSelectedSites, site)
                        }
                        className="text-primary-600 rounded"
                      />
                      {site}
                    </label>
                  ))}
              </div>
            )}
          </div>

          {/* アプリ */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              アプリ
            </label>
            <label className="flex items-center gap-1.5 text-sm mb-2">
              <input
                type="checkbox"
                checked={appsAll}
                onChange={(e) => {
                  setAppsAll(e.target.checked)
                  if (e.target.checked) setSelectedApps([])
                }}
                className="text-primary-600 rounded"
              />
              すべて許可 (*)
            </label>
            {!appsAll && (
              <div className="flex flex-wrap gap-2">
                {VALID_APPS.map((app) => (
                  <label
                    key={app}
                    className="flex items-center gap-1.5 text-sm bg-gray-50 px-2.5 py-1.5 rounded-md border border-gray-200 cursor-pointer hover:bg-gray-100"
                  >
                    <input
                      type="checkbox"
                      checked={selectedApps.includes(app)}
                      onChange={() =>
                        toggleItem(selectedApps, setSelectedApps, app)
                      }
                      className="text-primary-600 rounded"
                    />
                    {app}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* API */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              API
            </label>
            <label className="flex items-center gap-1.5 text-sm mb-2">
              <input
                type="checkbox"
                checked={apisAll}
                onChange={(e) => {
                  setApisAll(e.target.checked)
                  if (e.target.checked) setSelectedApis([])
                }}
                className="text-primary-600 rounded"
              />
              すべて許可 (*)
            </label>
            {!apisAll && (
              <div className="flex flex-wrap gap-2">
                {VALID_APIS.map((apiItem) => (
                  <label
                    key={apiItem}
                    className="flex items-center gap-1.5 text-sm bg-gray-50 px-2.5 py-1.5 rounded-md border border-gray-200 cursor-pointer hover:bg-gray-100"
                  >
                    <input
                      type="checkbox"
                      checked={selectedApis.includes(apiItem)}
                      onChange={() =>
                        toggleItem(selectedApis, setSelectedApis, apiItem)
                      }
                      className="text-primary-600 rounded"
                    />
                    {apiItem}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* 備考 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              備考
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="用途・担当者・期限などのメモ（任意）"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            キャンセル
          </button>
          <button
            onClick={handleSave}
            disabled={!matchValue.trim()}
            className="px-4 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            保存
          </button>
        </div>
      </div>
    </div>
  )
}
